import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { useProfile } from 'init/use-enforce-profile';
import { isDefined } from 'remeda';
import { addResourceNameToQueryResult } from 'shared/hooks/utils/add-resource-name-to-query-result';
import { useAxiosInstance } from 'shared/hooks/utils/axios-instance';
import type { ApiError } from 'shared/hooks/utils/types';
import { z } from 'zod/v4';

const MailCountSchema = z.object({
  draft: z.number(),
  unprocessed: z.number(),
});

export type MailCount = z.infer<typeof MailCountSchema>;

const EMPTY_MAIL_COUNT: MailCount = {
  draft: 0,
  unprocessed: 0,
};

async function loadMailCountFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;

  const { data } = await instance.get('/mails/count');
  return MailCountSchema.parse(data);
}

export function useLoadMailCount() {
  const axiosInstance = useAxiosInstance();
  const profile = useProfile();

  const queryResult = useQuery<MailCount, ApiError>({
    queryKey: ['mails', 'count'],
    queryFn: () => loadMailCountFn(axiosInstance),
    enabled: profile.hasAcceptedGc,
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return EMPTY_MAIL_COUNT;
    },
  });

  const result = addResourceNameToQueryResult<MailCount, ApiError, 'mailCount', 'PreventUndefined'>(
    'mailCount',
    queryResult,
    EMPTY_MAIL_COUNT,
  );

  // placeholderData only applies while the query is pending: if the request fails before ever
  // succeeding, `mailCount` becomes undefined even though the navbar mail menu reads
  // `mailCount.draft` unguarded and would crash on render. The fallback keeps `mailCount` defined
  // in that error state — the failure itself stays observable via mailCountError.
  return { ...result, mailCount: result.mailCount ?? EMPTY_MAIL_COUNT };
}
